import { com } from '@eidu/form'
import React, { useMemo, useState } from 'react'
import { Button, Card, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import ComplexPathField from './ComplexPathField'
import useRenderablePaths from '../misc/useRenderablePaths'
import useCreateSemanticLink from '../../io/useCreateSemanticLink'
import allSemanticLinkKeyConfigurations = com.eidu.sharedlib.entity.analytics.allSemanticLinkKeyConfigurations
import SemanticLinkKeyConfiguration = com.eidu.sharedlib.entity.analytics.SemanticLinkKeyConfiguration
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath
import semanticLinkKeyFromString = com.eidu.sharedlib.entity.analytics.semanticLinkKeyFromString
import isAllowedNonPredefinedKey = com.eidu.sharedlib.entity.analytics.isAllowedNonPredefinedKey
import customSemanticLinkKeyPrefixes = com.eidu.sharedlib.entity.analytics.customSemanticLinkKeyPrefixes
import FieldType = com.eidu.sharedlib.entity.field.FieldType

const CUSTOM_KEY_SENTINEL = '__custom__'
const exampleKeys = customSemanticLinkKeyPrefixes
  .asJsReadonlyArrayView()
  .map((p) => `${p}my-key`)
  .join(' or ')

type AddSemanticLinkFormProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onSave: () => void
  onCancel: () => void
}

const fieldTypeOptions: FieldType[] = [FieldType.Reference, FieldType.Text, FieldType.Number, FieldType.Date]

const AddSemanticLinkForm = ({ entityType, entityTypes, onSave, onCancel }: AddSemanticLinkFormProps) => {
  const predefinedConfigs = useMemo<SemanticLinkKeyConfiguration[]>(
    () =>
      allSemanticLinkKeyConfigurations
        .asJsReadonlyArrayView()
        .filter(({ onTypeKind }) => onTypeKind.equals(entityType.kind)),
    [entityType.kind]
  )

  const [selectedKeyValue, setSelectedKeyValue] = useState('')
  const [customKeyInput, setCustomKeyInput] = useState('')
  const [keyError, setKeyError] = useState<string | null>(null)

  const [fieldType, setFieldType] = useState<FieldType>(FieldType.Reference)
  const [selectedTargetTypeId, setSelectedTargetTypeId] = useState(entityTypes.at(0)?.id)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const { save, isSaving } = useCreateSemanticLink(onSave)

  const isCustom = selectedKeyValue === CUSTOM_KEY_SENTINEL
  const selectedPredefined = !isCustom
    ? predefinedConfigs.find((c) => c.key.toString() === selectedKeyValue)
    : undefined

  const resolvedKey = useMemo(() => {
    if (selectedPredefined) return selectedPredefined.key
    if (!isCustom) return null
    if (predefinedConfigs.some((c) => c.key.toString() === customKeyInput)) return null
    try {
      const key = semanticLinkKeyFromString(customKeyInput)
      return isAllowedNonPredefinedKey(key) ? key : null
    } catch {
      return null
    }
  }, [selectedPredefined, isCustom, predefinedConfigs, customKeyInput])

  const effectiveFieldType = selectedPredefined ? selectedPredefined.fieldType : fieldType

  const availableTargetEntityTypes = entityTypes.filter(({ kind }) => {
    const toTypeKind = selectedPredefined?.toTypeKind ?? null
    return toTypeKind ? kind.equals(toTypeKind) : true
  })

  const targetType = selectedTargetTypeId && entityTypes.find(({ id }) => id.equals(selectedTargetTypeId))
  const renderablePaths = useRenderablePaths(entityType.id, targetType, entityTypes)

  const handleSave = () => {
    if (resolvedKey && selectedPath) save(resolvedKey, selectedPath)
  }

  return (
    <Card>
      <Stack padding={2} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="semantic-link-key-label">Semantic link key</InputLabel>
          <Select
            labelId="semantic-link-key-label"
            value={selectedKeyValue}
            label="Semantic link key"
            onChange={(e) => {
              setSelectedKeyValue(e.target.value)
              setCustomKeyInput('')
              setKeyError(null)
              setSelectedPath(undefined)
            }}
          >
            {predefinedConfigs.map((config) => (
              <MenuItem key={config.key.toString()} value={config.key.toString()}>
                {config.title} ({config.key.toString()})
              </MenuItem>
            ))}
            <MenuItem value={CUSTOM_KEY_SENTINEL}>
              <Typography fontStyle="italic">Custom key...</Typography>
            </MenuItem>
          </Select>
        </FormControl>
        {isCustom && (
          <TextField
            fullWidth
            label="Custom key"
            placeholder={`e.g. ${exampleKeys}`}
            value={customKeyInput}
            error={!!keyError}
            helperText={keyError}
            onChange={(e) => {
              setCustomKeyInput(e.target.value)
              setKeyError(null)
            }}
            onBlur={() => {
              if (customKeyInput) {
                if (predefinedConfigs.some((c) => c.key.toString() === customKeyInput)) {
                  setKeyError('This key already exists as a predefined option — select it from the dropdown instead')
                } else {
                  try {
                    const key = semanticLinkKeyFromString(customKeyInput)
                    if (!isAllowedNonPredefinedKey(key)) {
                      setKeyError(`Keys in reserved namespaces must use a .custom. prefix, e.g. ${exampleKeys}`)
                    } else {
                      setKeyError(null)
                    }
                  } catch (e) {
                    setKeyError(`Invalid key: ${e instanceof Error ? e.message : String(e)}`)
                  }
                }
              }
            }}
          />
        )}
        {isCustom && (
          <FormControl fullWidth>
            <InputLabel id="field-type-label">Field type</InputLabel>
            <Select
              labelId="field-type-label"
              value={fieldType.toString()}
              label="Field type"
              onChange={(e) => {
                const selected = fieldTypeOptions.find((ft) => ft.toString() === e.target.value)
                if (selected) {
                  setFieldType(selected)
                  setSelectedPath(undefined)
                }
              }}
            >
              {fieldTypeOptions.map((ft) => (
                <MenuItem key={ft.toString()} value={ft.toString()}>
                  {ft.toString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {selectedPredefined && (
          <FormControl fullWidth>
            <InputLabel id="field-type-predefined-label">Field type</InputLabel>
            <Select
              labelId="field-type-predefined-label"
              value={selectedPredefined.fieldType.toString()}
              label="Field type"
              disabled
            >
              <MenuItem value={selectedPredefined.fieldType.toString()}>
                {selectedPredefined.fieldType.toString()}
              </MenuItem>
            </Select>
          </FormControl>
        )}
        {(selectedPredefined || isCustom) && (
          <>
            <EntityTypeField
              entityTypeId={selectedTargetTypeId}
              onChange={(type) => {
                setSelectedTargetTypeId(type.id)
                setSelectedPath(undefined)
              }}
              types={availableTargetEntityTypes}
              label="Target type"
            />
            <ComplexPathField
              entityTypeId={entityType.id}
              targetType={targetType}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPath}
              renderablePaths={renderablePaths}
              fieldType={effectiveFieldType}
            />
          </>
        )}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || !selectedPath || !resolvedKey}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}

export default AddSemanticLinkForm
